import React from 'react';
import { arrayOf, bool, number, object, string } from 'prop-types';
import { isEmpty, get } from 'lodash';
import {
  makeStyles,
  Checkbox,
  Link,
  TableContainer,
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from '@material-ui/icons';

import HTML5Backend from 'react-dnd-html5-backend';

import StatusContent from '../StatusContent';
import styles from './styles';
import { func } from 'prop-types';
import { DndProvider } from 'react-dnd';
import DraggableRow from './draggableRow';
import PlainTableRow from './plainTableRow';

export const getColumns = ({
  columns = [], hasBulkActions, isSelectedAll, isDisabledAll,
  onSelectAllRowsToggle, selectedStyles, selectedRows, allowOrdering,
}) => (
  <TableRow className={selectedStyles}>
    {hasBulkActions &&
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelectedAll}
          onChange={onSelectAllRowsToggle}
          inputProps={{ 'aria-label': 'select all rows' }}
          indeterminate={selectedRows.length > 0}
          disabled={isDisabledAll}
        />
      </TableCell>
    }
    {allowOrdering &&
      <TableCell>
          <ArrowUpwardIcon />
          <ArrowDownwardIcon />
      </TableCell>
    }
    {
      columns.map(({ id, label, minWidth, isHidden=false }) => {
        if(!isHidden) {
          return (
            <TableCell style={{ minWidth }} key={id}>
            {label}
            </TableCell>
          )
        }
      })
    }
  </TableRow>
);

export const getHyperlinkCellContent = ({ uuid, linkValue, format, value }) => (
    <Link underline="none" href={`${linkValue}/${uuid}`}>
      {format ? format(value) : value}
    </Link>
);

export const getPlainCellContent = ({ format, value, uuid, secondaryValue,
  isStatusIcon, notifyMessages, message, id, classes, isCellOverflow }) => {
   const content = format ? format(value, secondaryValue, uuid) : value;
  return(
    isStatusIcon ?
      <StatusContent
        id={id}
        statusLabel={content}
        notifyMessages={notifyMessages}
        message={message}
      />
    :
    <span className={isCellOverflow ? classes.cellOverflow: ''}>
      {content}
    </span>
  );
};

export const getHeaderCellContent = (rowHeaderColumn, row) => {
  const content = rowHeaderColumn.format(row);
 return(
   <span>
     {content}
   </span>
 );
};


export const getHeaderRows = ({
  userContext, columns = [], rows = [], uuidName, rowHeaderColumn, colsPanCount,
  columnContainerClass, notifyMessages, hasBulkActions, selectedStyles, numSelectedRows, classes,
}) => {

  return(
    rows.map(row => {
      const rows = get(rowHeaderColumn, 'secondaryId') ?
        get(row, rowHeaderColumn.secondaryId) : [];
      const childRowsContent = getRowsTable({
        userContext, columns, rows, uuidName,
        notifyMessages, hasBulkActions, selectedStyles, numSelectedRows, classes,
      })
      return (
        <>
          <TableRow key={row.uuid}>
            <TableCell
              key={rowHeaderColumn.id} colSpan={colsPanCount} className={columnContainerClass}>
              {getHeaderCellContent(rowHeaderColumn, row)}
            </TableCell>
          </TableRow>
          {childRowsContent}
        </>
      )
    })
  )
};

export const getRows = ({
  userContext, columns = [], rows = [], uuidName,
  notifyMessages, hasBulkActions, selectedStyles,
  numSelectedRows, classes, allowOrdering, onDragAndDrop,
}) =>
  allowOrdering ?
    rows.map((row, index) => (<DraggableRow key={row.uuid} {...{
      userContext, columns, rows, uuidName,
      notifyMessages, hasBulkActions, selectedStyles,
      numSelectedRows, classes, row, index, onDragAndDrop,
    }} />)) :
    rows.map(row => (<PlainTableRow key={row.uuid} {...{
      userContext, columns, rows, uuidName,
      notifyMessages, hasBulkActions, selectedStyles, numSelectedRows, classes, row,
    }} />));


const getRowsTable = ({
  userContext, columns = [], rows = [], uuidName,
  notifyMessages, hasBulkActions, selectedStyles, numSelectedRows, classes,
}) => (
  rows.map((row) =>
    <PlainTableRow key={ row.uuid } {...{ userContext, columns, uuidName,
      notifyMessages, hasBulkActions, selectedStyles, numSelectedRows, classes, row }} />));

function Table(props) {
  const {
    userContext,
    id,
    noResultsMessage,
    hasBulkActions,
    selectedRows = [],
    onSelectAllRowsToggle,
    allowOrdering,
    onDragAndDrop,
  } = props;
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const selectedStyles = (selectedRows.length > 0) ? classes.rowsSelected : '';
  const numSelectedRows = selectedRows.length;

  const rows = getRows({
    ...props,
    userContext,
    hasBulkActions,
    selectedStyles,
    numSelectedRows,
    classes,
    allowOrdering,
    onDragAndDrop,
  });
  const columns = getColumns({
    ...props,
    hasBulkActions,
    numRows: rows.length,
    numSelectedRows,
    onSelectAllRowsToggle,
    selectedStyles,
    allowOrdering,
  });

  return (
    <DndProvider backend={HTML5Backend}>
      <TableContainer
        className={classes.tableContainer}
        id={id} data-apim-test={id} data-layer7-test={id}
      >
        <MuiTable stickyHeader aria-label="sticky table">
          <TableHead>{columns}</TableHead>
          {isEmpty(rows) ?
            <caption>{noResultsMessage}</caption> :
            <TableBody>{rows}</TableBody>
          }
        </MuiTable>
      </TableContainer>
    </DndProvider>
  );
}

export default Table;

getColumns.propTypes = {
  columns: arrayOf(object),
};

getHyperlinkCellContent.propTypes = {
  uuid: string,
  linkValue: string,
  format: string,
  value: string || number,
};

getPlainCellContent.propTypes = {
  format: string, value: string || number,
};

Table.propTypes = {
  userContext: object,
  id: string,
  noResultsMessage: string,
  hasBulkActions: bool,
  selectedRows: arrayOf(string),
  onSelectAllRowsToggle: func,
  allowOrdering: bool,
  onDragAndDrop: func,
};